import { useTranslation } from 'next-i18next';
import { FC, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux';
import { loadPaymentProvider } from '@/redux/thunks/checkout/loadPaymentProvider';
import { isApplePayAvailable, selectPaymentProviderInfos } from '@/redux/slices/checkout.slice';
import { selectIsUserConnected } from '@/redux/slices/auth.slice';
import { useFeature } from '@/hooks';

import { NS } from '@/constants';

import styles from './ApplePayButton.module.scss';
import { FEATURES } from '@/constants/features';
import { PAYMENT_PLATFORMS } from '@/constants/checkout';

export const ApplePayButton: FC = () => {
  const { t } = useTranslation(NS.COMMON);
  const applePayRef = useRef(null);
  const dispatch = useAppDispatch();
  const shouldShow = useRef(true);
  const isPaymentProviderLoaded = useAppSelector(selectPaymentProviderInfos).isLoaded;
  const userIsNotConnected = !useAppSelector(selectIsUserConnected);

  const storeConf = useAppSelector(({ root }) => root.storeConf);

  const isHttpsConnection = typeof window !== 'undefined' && window?.location.protocol === 'https:';
  const hasApplePaySession = typeof window !== 'undefined' && 'ApplePaySession' in window;
  const canMakePayments =
    isHttpsConnection &&
    hasApplePaySession &&
    typeof ApplePaySession !== 'undefined' &&
    ApplePaySession?.canMakePayments();
  const isFeatureEnabled = useFeature(storeConf, FEATURES.APPLE_QUICK_PAY);

  useEffect(() => {
    if (
      !isHttpsConnection || userIsNotConnected ||
      !canMakePayments || !isFeatureEnabled
    ) {
      dispatch(isApplePayAvailable({ isApplePayAvailable: false }));
      shouldShow.current = false;
    }

    if (shouldShow.current && !isPaymentProviderLoaded) {
      dispatch(isApplePayAvailable({ isApplePayAvailable: true }));
      dispatch(loadPaymentProvider({
        paymentProvider: PAYMENT_PLATFORMS.ADYEN_APPLE_QUICK_PAY, binding: applePayRef.current, isQuickPay: true,
        t
      }));
    }
  }, [isPaymentProviderLoaded]);

  if (!shouldShow.current) {
    return null;
  }

  const classNames = [
    isPaymentProviderLoaded ? styles.apple : styles.loading
  ].join(' ');

  return <div className={classNames} ref={applePayRef} />;
};
